import { Col, Flex, Progress, Row, Select, Skeleton, Statistic, Typography, Table, Button } from 'antd';
import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { FileOutlined } from '@ant-design/icons';
import { BASE_URL } from '../utils/routes';
import html2pdf from 'html2pdf.js';

const CourseList = () => {
    const [orderItems, setOrderItems] = useState([]);
    const [loadingCourses, setLoadingCourses] = useState(true);
    const [token, setToken] = useState(Cookies.get('token'));
    const [selectedCourse, setSelectedCourse] = useState(null);
    const [loadingSelectedCourse, setLoadingSelectedCourse] = useState(false);
    const [users, setUsers] = useState([]);

    const fetchOrderItems = async () => {
        try {
            setLoadingCourses(true);
            const response = await fetch(`${BASE_URL}wp-json/custom/v1/purchased-courses`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await response.json();
            const options = data
                .filter((item) => item.item_id !== null)
                .map((item) => ({
                    value: item.item_id,
                    label: item.course_info.post_title,
                }));
            setOrderItems(options);
        } catch (err) {
            console.error('Error fetching order items:', err);
        } finally {
            setLoadingCourses(false);
        }
    };

    const fetchUsers = async (value) => {
        try {
            setLoadingSelectedCourse(true);
            const response = await fetch(`${BASE_URL}wp-json/custom/v1/students/${value}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = await response.json();
            setUsers(data);
        } catch (e) {
            console.error('Error fetching users:', e);
        } finally {
            setLoadingSelectedCourse(false);
        }
    }

    useEffect(() => {
        fetchOrderItems();
    }, [token]);

    const handleSelectChange = (value) => {
        setSelectedCourse(value);
        fetchUsers(value);
    };

    // Render quiz progress details
    const renderQuizProgress = (quizProgress) => {
        // Transform the quiz progress object into an array for table rendering
        const quizProgressArray = Object.entries(quizProgress).map(([quiz_id, details]) => ({
            quiz_id,
            status: details.status,
            graduation: details.graduation,
        }));

        const columns = [
            {
                title: 'Quiz ID',
                dataIndex: 'quiz_id',
                key: 'quiz_id',
            },
            {
                title: 'Status',
                dataIndex: 'status',
                key: 'status',
                render: (status) => {
                    const statusMap = {
                        'completed': <Typography.Text type="success">Completed</Typography.Text>,
                        'started': <Typography.Text type="warning">Started</Typography.Text>,
                        'not_started': <Typography.Text type="secondary">Not Started</Typography.Text>,
                    };
                    return statusMap[status] || status;
                }
            },
            {
                title: 'Graduation',
                dataIndex: 'graduation',
                key: 'graduation',
                render: (graduation) => {
                    const graduationMap = {
                        'passed': <Typography.Text type="success">Passed</Typography.Text>,
                        'in-progress': <Typography.Text type="warning">In Progress</Typography.Text>,
                        'failed': <Typography.Text type="danger">Failed</Typography.Text>,
                    };
                    return graduationMap[graduation] || graduation;
                }
            }
        ];

        return (
            <Table 
                columns={columns} 
                dataSource={quizProgressArray} 
                rowKey="quiz_id"
                size="small"
                pagination={false}
            />
        );
    };

    const toPDF = () => {
        const element = document.getElementById('pdf-content');
        const opt = {
          margin: 0.5,
          filename: 'thummim-export.pdf',
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { scale: 2, useCORS: true },
          jsPDF: { unit: 'in', format: 'letter', orientation: 'landscape' }
        };
      
        html2pdf().from(element).set(opt).save();
      };
      

    return (
        <>
            {loadingCourses ? (
                <Skeleton active />
            ) : (
                <>
                    <h2>Course List</h2>
                    <Select
                        showSearch
                        style={{ width: '500px' }}
                        placeholder="Search a course"
                        filterOption={(input, option) =>
                            option.label.toLowerCase().includes(input.toLowerCase())
                        }
                        options={orderItems.map(item => ({
                            key: item.value,
                            value: item.value,
                            label: item.label
                        }))}
                        onChange={handleSelectChange}
                        value={selectedCourse}
                    />
                    {selectedCourse && (
                        <>
                            {loadingSelectedCourse ? (
                                <Skeleton active />
                            ) : (
                                <>
                                    {users && (
                                        <Flex vertical style={{ padding: '2rem' }}>
                                            <Flex justify='center'>
                                                <Statistic title="Students" value={users.length} />
                                            </Flex>
                                            {users.length < 100 && (
                                                <Flex style={{marginBottom: '2rem'}} justify='end'>
                                                <Button onClick={toPDF}>Download PDF</Button>
                                                </Flex>
                                            )}
                                            <div id="pdf-content">
                                            <Row gutter={[16, 16]} justify="center">
                                                {users.map((item, index) => (
                                                    <Col
                                                    xs={24} sm={12} md={12} lg={6} xl={6}
                                                    key={index}
                                                >
                                                        <div style={{ 
                                                            padding: '1rem', 
                                                            border: '1px solid #f0f0f0', 
                                                            borderRadius: '8px',
                                                            marginBottom: '16px'
                                                        }}>
                                                            <Flex gap={15} align="center">
                                                                <Flex vertical justify="start" align="start" style={{ width: '100%' }}>
                                                                    <Typography.Title level={4}>
                                                                        {item.name || 'N/A'}
                                                                    </Typography.Title>
                                                                    <Typography.Text style={{textAlign: 'left', display: 'inline'}}>
                                                                        Email: {item.meta.data.user_email}
                                                                    </Typography.Text>
                                                                    <Typography.Text>
                                                                        Date Registered: {item.meta.data.user_registered}
                                                                    </Typography.Text>
                                                                    <Typography.Text>
                                                                        Course Progress
                                                                    </Typography.Text>
                                                                    <Progress percent={item.progress} />
                                                                    <Typography.Text>
                                                                        Status: {item.status}
                                                                    </Typography.Text>

                                                                    {/* Attachments Section */}
                                                                    <div style={{ marginTop: '8px', width: '100%' }}>
                                                                        <Typography.Text strong>
                                                                            Attachments:
                                                                        </Typography.Text>
                                                                        {item.attachments && item.attachments.length > 0 ? (
                                                                            <ul style={{ 
                                                                                listStyle: 'none', 
                                                                                padding: '8px 0 0 0',
                                                                                margin: 0
                                                                            }}>
                                                                                {item.attachments.map((attachment, idx) => (
                                                                                    <li key={idx}>
                                                                                        <a
                                                                                            href={attachment.file_url}
                                                                                            target="_blank"
                                                                                            rel="noopener noreferrer"
                                                                                            style={{
                                                                                                display: 'flex',
                                                                                                alignItems: 'center',
                                                                                                gap: '4px',
                                                                                                color: '#1890ff'
                                                                                            }}
                                                                                        >
                                                                                            <FileOutlined />
                                                                                            {attachment.file_name}
                                                                                        </a>
                                                                                    </li>
                                                                                ))}
                                                                            </ul>
                                                                        ) : (
                                                                            <Typography.Text type="secondary" style={{ display: 'block', paddingTop: '4px' }}>
                                                                                No attachments
                                                                            </Typography.Text>
                                                                        )}
                                                                    </div>

                                                                    {/* Quiz Progress Section */}
                                                                    <div style={{ marginTop: '16px', width: '100%' }}>
                                                                        <Typography.Title level={5}>
                                                                            Quiz Progress
                                                                        </Typography.Title>
                                                                        {item.quiz_progress && Object.keys(item.quiz_progress).length > 0 ? (
                                                                            renderQuizProgress(item.quiz_progress)
                                                                        ) : (
                                                                            <Typography.Text type="secondary">
                                                                                No quiz data available
                                                                            </Typography.Text>
                                                                        )}
                                                                    </div>
                                                                </Flex>
                                                            </Flex>
                                                        </div>
                                                    </Col>
                                                ))}
                                            </Row>
                                            </div>
                                        </Flex>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </>
    );
};

export default CourseList;